import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Award, Search, ShieldCheck, ShieldX } from "lucide-react";

export default function VerifyCertificate() {
  const { certId } = useParams();
  const nav = useNavigate();
  const { brand } = useWorkspace();
  const [query, setQuery] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(async (id) => {
    if (!id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(id.trim())}`);
      setResult(data);
    } catch {
      setResult({ valid: false });
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { if (certId) lookup(certId); }, [certId, lookup]);

  const submit = (e) => {
    e.preventDefault();
    nav(`/verify/${encodeURIComponent(query.trim().toUpperCase())}`);
    lookup(query);
  };

  return (
    <div className="min-h-screen bg-[color:var(--brand-bg)] text-[var(--brand-primary)] flex flex-col">
      <header className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="verify-brand-home">
            <div className="w-8 h-8 flex items-center justify-center" style={{ border: "1px solid var(--brand-primary)" }}>
              <span className="font-display font-semibold text-xs tracking-tight">{brand.logo_text}</span>
            </div>
            <div>
              <div className="font-display text-base leading-tight">{brand.name}</div>
              <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Certificate Verification</div>
            </div>
          </Link>
          <Link to="/login" className="text-sm link-underline" data-testid="verify-nav-login">Sign in</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl">
          <div className="label-eyebrow mb-3">Authenticity check</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">Verify a certificate</h1>
          <p className="text-neutral-500 mb-8">Enter a certificate ID (e.g. EPL-2026-A1B2C3) to confirm it was officially issued.</p>

          <form onSubmit={submit} className="flex gap-2 mb-8" data-testid="verify-form">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Certificate ID"
              className="flex-1 bg-transparent border hairline px-4 py-3 font-mono text-sm uppercase focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
              data-testid="verify-input"
            />
            <button disabled={busy || !query.trim()} type="submit"
                    className="inline-flex items-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-3 font-semibold rounded-sm transition-colors disabled:opacity-60"
                    data-testid="verify-submit">
              <Search size={15} /> {busy ? "Checking…" : "Verify"}
            </button>
          </form>

          {result && (
            result.valid ? (
              <div className="border-2 border-[color:var(--brand-primary)] bg-white p-8" data-testid="verify-valid">
                <div className="flex items-center gap-3 mb-6">
                  <ShieldCheck size={22} className="text-[var(--brand-primary)]" />
                  <span className="label-eyebrow text-[var(--brand-primary)]">Certificate verified · Authentic</span>
                </div>
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 flex items-center justify-center shrink-0" style={{ border: "1.5px solid var(--brand-primary)" }}>
                    <Award size={26} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Awarded to</div>
                      <div className="font-display text-2xl" data-testid="verify-trainee-name">{result.trainee_name}</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Issued by</div>
                        <div data-testid="verify-workspace">{result.workspace_name}</div>
                      </div>
                      <div>
                        <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Date</div>
                        <div data-testid="verify-date">{new Date(result.issued_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</div>
                      </div>
                    </div>
                    <div>
                      <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>Certificate ID</div>
                      <div className="font-mono text-sm text-[var(--brand-primary)]" data-testid="verify-cert-id">{result.certificate_id}</div>
                    </div>
                    <div className="text-xs text-neutral-500 pt-1">Six-stage onboarding curriculum · Methodology · Market · Product · Admin · Metrics · The Lab</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-[#8B2E2E] bg-[#8B2E2E]/5 p-8" data-testid="verify-invalid">
                <div className="flex items-center gap-3 mb-3">
                  <ShieldX size={22} className="text-[#8B2E2E]" />
                  <span className="label-eyebrow text-[#8B2E2E]">Not found</span>
                </div>
                <div className="font-display text-xl leading-tight">No certificate matches that ID.</div>
                <p className="text-sm text-neutral-600 mt-2">Check the ID for typos. If you believe this is an error, contact the issuing organisation.</p>
              </div>
            )
          )}
        </div>
      </main>

      <footer className="border-t hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 text-xs text-neutral-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} {brand.name}</div>
          <div className="label-eyebrow">Certificate verification</div>
        </div>
      </footer>
    </div>
  );
}
